import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAvatar,
  MiButton,
  MiIcon,
  MiInputTextbox,
  MiInternalDrawer,
  MiSectionHeader,
} from "@miplatform/components";
import {
  ICONS_WEB,
  SECTION_HEADER_VARIANTS,
  TECHNICIAN_ACTIVITY_STATUSES_KEYED,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import {
  addAlpha,
  getInitials,
  getRootUrl,
  toKebabCase,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { InputAdornment, Typography } from "@mui/material";
import { Box } from "@mui/system";

const TechnicianCard = ({ user, onSelect, displayOrder, isSelected }) => {
  const { name, activityStatus, assignedDispatcherDisplayName } = user;
  const { theme, classes } = useStyles({
    assignedDispatcherDisplayName,
    isSelected,
  });
  const activityStatusObj = TECHNICIAN_ACTIVITY_STATUSES_KEYED[activityStatus];

  return (
    <Box
      className={classes.userCardContainer}
      onClick={() => onSelect(user)}
      data-testid={`user-card-${toKebabCase(name)}`}
    >
      <Box className={classes.userCardNameContainer}>
        <MiAvatar
          className={classes.avatar}
          data-testid="user-initials"
          src={
            user?.profilePicture
              ? `${getRootUrl()}/document-service/documents/${
                  user?.profilePicture.id
                }`
              : undefined
          }
        >
          {getInitials(name)}
        </MiAvatar>
        <Box className={classes.userCardContentContainer}>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            fontWeight={theme.fontWeight.bold}
          >{`${displayOrder ? `${displayOrder}. ` : ""}${name}`}</Typography>
          <Box display="flex" flexDirection="row">
            <Box
              className={classes.statusContainer}
              backgroundColor={addAlpha(activityStatusObj.color, 25)}
              border={`0.5px solid ${activityStatusObj.color}`}
            >
              <MiIcon
                size={0.6}
                path={mdi[activityStatusObj.icon]}
                color={activityStatusObj.color}
                data-testid="status-icon"
              />
              <Typography
                color={activityStatusObj.color}
                variant={TYPOGRAPHY_VARIANTS.CAPTION}
                data-testid="status-text"
                lineHeight={0}
                fontWeight={theme.fontWeight.bold}
              >
                {activityStatusObj.label ?? activityStatus}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const SearchTechnician = ({ search, setSearch }) => {
  const { classes } = useStyles();

  return (
    <MiInputTextbox
      className={classes.searchContainer}
      placeholder="Search Technicians"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      textfieldProps={{
        startAdornment: (
          <InputAdornment position="start" style={{ margin: 0 }}>
            <MiIcon path={mdi[ICONS_WEB.MAGNIFY]} />
          </InputAdornment>
        ),
        endAdornment: search && (
          <InputAdornment position="end" style={{ margin: 0 }}>
            <MiButton
              startIcon={{ path: mdi[ICONS_WEB.CLOSE] }}
              className={classes.iconButton}
              onClick={() => setSearch("")}
              data-testid="clear-search-btn"
            />
          </InputAdornment>
        ),
        className: classes.searchInput,
        "data-testid": "technician-search-input",
      }}
    />
  );
};

export const TechnicianList = ({
  debouncedSearch,
  users,
  onSelect,
  selectedUserId,
}) => {
  const filteredUsers = useMemo(
    () =>
      users.filter((user) =>
        user.name?.toLowerCase().includes(debouncedSearch.toLowerCase())
      ),
    [users, debouncedSearch]
  );

  return (
    <Box>
      {filteredUsers.length ? (
        filteredUsers.map((user, i) => (
          <TechnicianCard
            key={user.id}
            user={user}
            onSelect={onSelect}
            displayOrder={i + 1}
            isSelected={selectedUserId === user.id}
          />
        ))
      ) : (
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BODY2}
          align="center"
          marginTop={2}
          color="text.secondary"
        >
          No technicians found.
        </Typography>
      )}
    </Box>
  );
};

export function TechnicianSelectorDrawer({
  users = [],
  selectedUserId,
  onUserSelect,
  open,
  onClose,
}) {
  const { theme, classes } = useStyles();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);
  return (
    <MiInternalDrawer open={open} className={classes.drawer} width={255}>
      <Box className={classes.container}>
        <Box
          position="sticky"
          top={0}
          zIndex={theme.zIndex.drawer}
          backgroundColor="background.paper"
          borderBottom={`1px solid ${theme.palette.grey.accent}`}
        >
          <MiSectionHeader
            removeMargin
            headerStyle={{ padding: theme.spacing(1, 2) }}
            variant={SECTION_HEADER_VARIANTS.TEXT}
            title="Technician Selector"
            rightIcon={mdi[ICONS_WEB.CLOSE]}
            onRightIconClick={onClose}
          />
          <SearchTechnician search={search} setSearch={setSearch} />
        </Box>
        <TechnicianList
          debouncedSearch={debouncedSearch}
          onSelect={onUserSelect}
          users={users}
          selectedUserId={selectedUserId}
        />
      </Box>
    </MiInternalDrawer>
  );
}

const useStyles = makeStyles()(
  (theme, { assignedDispatcherDisplayName, isSelected } = {}) => ({
    drawer: {
      borderRight: `1px solid ${theme.palette.grey.lightAccent}`,
    },
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },

    iconButton: {
      padding: theme.spacing(1),
    },
    searchContainer: {
      padding: theme.spacing(0, 2),
      margin: theme.spacing(1, 0),
    },
    searchInput: {
      padding: theme.spacing(0, 1),
      margin: 0,
      height: 32,
      fontSize: theme.fontSize.medium,
    },
    userCardContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.grey.accent}`,
      cursor: "pointer",
      backgroundColor: isSelected
        ? theme.palette.orange.mediumLight
        : theme.palette.common.white,
    },
    userCardNameContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    userCardContentContainer: {
      display: "flex",
      flexDirection: "column",
      marginLeft: theme.spacing(0.5),
    },
    statusContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      width: "fit-content",
      padding: theme.spacing(0.5),
      gap: theme.spacing(0.5),
    },
    dispatcherContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      marginLeft: theme.spacing(1),
      fontSize: theme.fontSize.small,
    },
    dispatcherDisplayName: {
      fontWeight: theme.fontWeight.bold,
      maxWidth: 88,
      color: assignedDispatcherDisplayName
        ? theme.palette.blue.main
        : theme.palette.grey.main,
    },
    avatar: {
      width: 24,
      height: 24,
      borderRadius: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      fontSize: theme.fontSize.small,
    },
  })
);
